import { useCallback, useEffect, useState } from 'react';

/**
 * PWA install prompt state. `mode` is one of:
 *
 * - 'native': Chromium fired beforeinstallprompt — install() opens the real dialog.
 * - 'ios': iOS browser (all are WebKit, no prompt API) — show Share-menu steps.
 * - 'android-manual': Firefox on Android — show menu steps / open-in-Chrome link.
 * - null: already installed, running standalone, or dismissed within the last 30 days.
 */

const DISMISSED_AT_KEY = 'pwaInstallDismissedAt';
const INSTALLED_KEY = 'pwaInstalled';
const SNOOZE_MS = 30 * 24 * 60 * 60 * 1000;

function isSnoozed() {
  const dismissedAt = Number(localStorage.getItem(DISMISSED_AT_KEY));
  return dismissedAt > 0 && Date.now() - dismissedAt < SNOOZE_MS;
}

export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    navigator.standalone === true
  );
}

export function usePwaInstall() {
  const [nativePrompt, setNativePrompt] = useState(null);
  const [hidden, setHidden] = useState(
    () => isStandalone() || isSnoozed() || localStorage.getItem(INSTALLED_KEY) === '1',
  );

  useEffect(() => {
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setNativePrompt(e);
    };
    const onAppInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, '1');
      setHidden(true);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISSED_AT_KEY, String(Date.now()));
    setHidden(true);
  }, []);

  const install = useCallback(async () => {
    if (!nativePrompt) return;
    await nativePrompt.prompt();
    const { outcome } = await nativePrompt.userChoice;
    setNativePrompt(null);
    if (outcome === 'dismissed') dismiss();
    else setHidden(true);
  }, [nativePrompt, dismiss]);

  const ua = navigator.userAgent;
  // iPadOS reports itself as MacIntel; the touch check tells it apart from macOS.
  const ios =
    /iphone|ipad|ipod/i.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const firefoxAndroid = /android/i.test(ua) && /firefox/i.test(ua);

  let mode = null;
  if (!hidden) {
    if (nativePrompt) mode = 'native';
    else if (ios) mode = 'ios';
    else if (firefoxAndroid) mode = 'android-manual';
  }

  return { mode, install, dismiss };
}
